// apps/web/src/pages/maintenance/AssetsPage.tsx
import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, ChevronDown, ChevronRight, Upload, FileCheck, Pencil } from 'lucide-react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Card } from '../../components/ui/Card'
import api from '../../lib/api'

// ---- Types ----
interface Company { id: string; name: string }
interface Site { id: string; name: string }
interface AssetInstance {
  id: string
  serialNumber: string | null
  partNumber: string | null
  location: string | null
  notes: string | null
}
interface Asset {
  id: string
  siteId: string | null
  category: 'hardware' | 'software'
  name: string
  assetType: string | null
  model: string | null
  manufacturer: string | null
  softwareRelease: string | null
  hostMachine: string | null
  firmwareFileKey: string | null
  firmwareFileName: string | null
  backupFileKey: string | null
  backupFileName: string | null
  instances: AssetInstance[]
}

// ---- Helpers ----
const emptyHW = { name: '', assetType: '', model: '', manufacturer: '' }
const emptySW = { name: '', softwareRelease: '', hostMachine: '' }
const emptyInst = { serialNumber: '', partNumber: '', location: '', notes: '' }

function FileDropZone({ label, onFile, currentName, uploading }: {
  label: string
  onFile: (f: File) => void
  currentName: string | null
  uploading?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  return (
    <div
      onClick={() => inputRef.current?.click()}
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => {
        e.preventDefault(); setDragging(false)
        const f = e.dataTransfer.files[0]
        if (f) onFile(f)
      }}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-3 py-2 text-xs transition-colors ${
        dragging
          ? 'border-[var(--fc-primary)] bg-[color-mix(in_srgb,var(--fc-primary)_5%,transparent)]'
          : 'border-[var(--fc-border)] hover:border-[var(--fc-primary)]'
      }`}
    >
      <input ref={inputRef} type="file" className="hidden" onChange={e => {
        const f = e.target.files?.[0]
        if (f) onFile(f)
      }} />
      {uploading ? (
        <span className="text-[var(--fc-foreground-muted)]">Caricamento...</span>
      ) : currentName ? (
        <div className="flex items-center gap-1 text-green-600">
          <FileCheck className="h-3.5 w-3.5 shrink-0" />
          <span className="truncate max-w-[150px]">{currentName}</span>
        </div>
      ) : (
        <div className="flex items-center gap-1 text-[var(--fc-foreground-muted)]">
          <Upload className="h-3.5 w-3.5 shrink-0" />
          <span>{label}</span>
        </div>
      )}
    </div>
  )
}

// ---- Main Component ----
export function AssetsPage() {
  const qc = useQueryClient()
  const [selectedCompany, setSelectedCompany] = useState('')
  const [selectedSite, setSelectedSite] = useState('')
  const [activeTab, setActiveTab] = useState<'hardware' | 'software'>('hardware')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})

  // Hardware form
  const [showHWForm, setShowHWForm] = useState(false)
  const [hwForm, setHWForm] = useState(emptyHW)
  const [formCompany, setFormCompany] = useState('')
  const [formSiteId, setFormSiteId] = useState('')

  // Software form
  const [showSWForm, setShowSWForm] = useState(false)
  const [swForm, setSWForm] = useState(emptySW)
  const [swFormCompany, setSWFormCompany] = useState('')
  const [swFormSiteId, setSWFormSiteId] = useState('')

  // Instance forms per asset
  const [showInstForm, setShowInstForm] = useState<Record<string, boolean>>({})
  const [instForm, setInstForm] = useState<Record<string, typeof emptyInst>>({})

  // Uploading state per asset
  const [uploading, setUploading] = useState<Record<string, boolean>>({})

  // Inline edit state for hardware/software assets
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<typeof emptyHW & typeof emptySW>>({})

  // Inline edit state for instances
  const [editingInstId, setEditingInstId] = useState<string | null>(null)
  const [editInstForm, setEditInstForm] = useState<typeof emptyInst>(emptyInst)
  const [editingInstAssetId, setEditingInstAssetId] = useState<string | null>(null)

  // Queries
  const { data: companies = [] } = useQuery({
    queryKey: ['companies', 'list'],
    queryFn: async () => (await api.get<Company[]>('/companies')).data,
  })
  const { data: filterSites = [] } = useQuery({
    queryKey: ['sites', selectedCompany],
    queryFn: async () => (await api.get<Site[]>('/sites', { params: { companyId: selectedCompany } })).data,
    enabled: Boolean(selectedCompany),
  })
  const { data: formSitesHW = [] } = useQuery({
    queryKey: ['sites', formCompany],
    queryFn: async () => (await api.get<Site[]>('/sites', { params: { companyId: formCompany } })).data,
    enabled: Boolean(formCompany),
  })
  const { data: formSitesSW = [] } = useQuery({
    queryKey: ['sites', swFormCompany],
    queryFn: async () => (await api.get<Site[]>('/sites', { params: { companyId: swFormCompany } })).data,
    enabled: Boolean(swFormCompany),
  })
  const { data: assets = [], isLoading } = useQuery({
    queryKey: ['assets', selectedSite, selectedCompany, activeTab],
    queryFn: async () => {
      const params: Record<string, string> = { category: activeTab }
      if (selectedSite) params['siteId'] = selectedSite
      else if (selectedCompany) params['companyId'] = selectedCompany
      return (await api.get<Asset[]>('/assets', { params })).data
    },
    enabled: Boolean(selectedCompany),
  })

  // Mutations
  const createHW = useMutation({
    mutationFn: () => api.post('/assets', { ...hwForm, category: 'hardware', siteId: formSiteId || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] })
      setShowHWForm(false); setHWForm(emptyHW); setFormCompany(''); setFormSiteId('')
    },
  })
  const createSW = useMutation({
    mutationFn: () => api.post('/assets', { ...swForm, category: 'software', siteId: swFormSiteId || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] })
      setShowSWForm(false); setSWForm(emptySW); setSWFormCompany(''); setSWFormSiteId('')
    },
  })
  const deleteAsset = useMutation({
    mutationFn: (id: string) => api.delete(`/assets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assets'] }),
  })
  const createInst = useMutation({
    mutationFn: ({ assetId, data }: { assetId: string; data: typeof emptyInst }) =>
      api.post(`/assets/${assetId}/instances`, data),
    onSuccess: (_, { assetId }) => {
      qc.invalidateQueries({ queryKey: ['assets'] })
      setShowInstForm(p => ({ ...p, [assetId]: false }))
      setInstForm(p => ({ ...p, [assetId]: emptyInst }))
    },
  })
  const deleteInst = useMutation({
    mutationFn: ({ assetId, instanceId }: { assetId: string; instanceId: string }) =>
      api.delete(`/assets/${assetId}/instances/${instanceId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['assets'] }),
  })

  // Edit asset (hardware or software)
  const updateAsset = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, string> }) =>
      api.put(`/assets/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] })
      setEditingId(null)
      setEditForm({})
    },
  })

  // Edit instance
  const updateInst = useMutation({
    mutationFn: ({ assetId, instanceId, data }: { assetId: string; instanceId: string; data: typeof emptyInst }) =>
      api.put(`/assets/${assetId}/instances/${instanceId}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] })
      setEditingInstId(null)
      setEditingInstAssetId(null)
      setEditInstForm(emptyInst)
    },
  })

  async function handleFileUpload(assetId: string, file: File, type: 'firmware' | 'backup') {
    setUploading(p => ({ ...p, [assetId]: true }))
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.post(`/assets/${assetId}/${type}`, fd)
      qc.invalidateQueries({ queryKey: ['assets'] })
    } finally {
      setUploading(p => ({ ...p, [assetId]: false }))
    }
  }

  const toggleExpand = (id: string) => setExpanded(p => ({ ...p, [id]: !p[id] }))

  function startEditAsset(asset: Asset) {
    setEditingId(asset.id)
    if (asset.category === 'hardware') {
      setEditForm({
        name: asset.name,
        assetType: asset.assetType ?? '',
        model: asset.model ?? '',
        manufacturer: asset.manufacturer ?? '',
      })
    } else {
      setEditForm({
        name: asset.name,
        softwareRelease: asset.softwareRelease ?? '',
        hostMachine: asset.hostMachine ?? '',
      })
    }
  }

  function startEditInst(inst: AssetInstance, assetId: string) {
    setEditingInstId(inst.id)
    setEditingInstAssetId(assetId)
    setEditInstForm({
      serialNumber: inst.serialNumber ?? '',
      partNumber: inst.partNumber ?? '',
      location: inst.location ?? '',
      notes: inst.notes ?? '',
    })
  }

  const inlineInput = (
    value: string,
    onChange: (v: string) => void,
    placeholder?: string,
    extraClass?: string
  ) => (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`rounded border border-[var(--fc-border)] bg-[var(--fc-surface)] px-2 py-1 text-sm w-full ${extraClass ?? ''}`}
    />
  )

  return (
    <div className="space-y-6">
      <PageHeader title="Parco macchine" subtitle="Hardware e Software per sito cliente" />

      {/* Filtro cliente → sito */}
      <Card>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Cliente *</label>
            <select
              value={selectedCompany}
              onChange={e => { setSelectedCompany(e.target.value); setSelectedSite('') }}
              className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
            >
              <option value="">— seleziona cliente —</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Sito</label>
            <select
              value={selectedSite}
              onChange={e => setSelectedSite(e.target.value)}
              disabled={!selectedCompany}
              className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm disabled:opacity-50"
            >
              <option value="">Tutti i siti</option>
              {filterSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
      </Card>

      {!selectedCompany && (
        <p className="text-sm text-[var(--fc-foreground-muted)]">Seleziona un cliente per visualizzare il parco macchine.</p>
      )}

      {selectedCompany && (
        <>
          {/* Tab switcher */}
          <div className="flex gap-0 border-b border-[var(--fc-border)]">
            {(['hardware', 'software'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-5 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  activeTab === tab
                    ? 'border-[var(--fc-primary)] text-[var(--fc-primary)]'
                    : 'border-transparent text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]'
                }`}
              >
                {tab === 'hardware' ? 'Hardware' : 'Software'}
              </button>
            ))}
          </div>

          {/* ---- HARDWARE TAB ---- */}
          {activeTab === 'hardware' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => { setShowHWForm(true); setFormCompany(selectedCompany); setFormSiteId(selectedSite) }}
                  className="flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white"
                >
                  <Plus className="h-4 w-4" /> Nuovo prodotto
                </button>
              </div>

              {showHWForm && (
                <Card>
                  <h3 className="text-base font-semibold mb-4">Nuovo prodotto hardware</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Cliente</label>
                      <select
                        value={formCompany}
                        onChange={e => { setFormCompany(e.target.value); setFormSiteId('') }}
                        className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                      >
                        <option value="">— seleziona —</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Sito</label>
                      <select
                        value={formSiteId}
                        onChange={e => setFormSiteId(e.target.value)}
                        disabled={!formCompany}
                        className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm disabled:opacity-50"
                      >
                        <option value="">— seleziona —</option>
                        {formSitesHW.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    {([
                      { key: 'name' as const, label: 'Nome prodotto *' },
                      { key: 'assetType' as const, label: 'Tipo (es. Server, Switch…)' },
                      { key: 'model' as const, label: 'Modello' },
                      { key: 'manufacturer' as const, label: 'Produttore' },
                    ]).map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">{label}</label>
                        <input
                          value={hwForm[key]}
                          onChange={e => setHWForm(p => ({ ...p, [key]: e.target.value }))}
                          className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => createHW.mutate()}
                      disabled={!hwForm.name || !formCompany || !formSiteId || createHW.isPending}
                      className="rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {createHW.isPending ? 'Salvataggio...' : 'Salva prodotto'}
                    </button>
                    <button onClick={() => setShowHWForm(false)} className="rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm">
                      Annulla
                    </button>
                  </div>
                </Card>
              )}

              {isLoading ? (
                <p className="text-sm text-[var(--fc-foreground-muted)]">Caricamento...</p>
              ) : assets.length === 0 ? (
                <p className="text-sm text-[var(--fc-foreground-muted)]">Nessun prodotto hardware. Clicca "Nuovo prodotto" per iniziare.</p>
              ) : (
                <div className="space-y-3">
                  {assets.map(asset => (
                    <Card key={asset.id}>
                      {/* Header prodotto — edit mode */}
                      {editingId === asset.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            {([
                              { key: 'name' as const, label: 'Nome prodotto *' },
                              { key: 'assetType' as const, label: 'Tipo' },
                              { key: 'model' as const, label: 'Modello' },
                              { key: 'manufacturer' as const, label: 'Produttore' },
                            ]).map(({ key, label }) => (
                              <div key={key}>
                                <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">{label}</label>
                                {inlineInput(
                                  (editForm as Record<string, string>)[key] ?? '',
                                  v => setEditForm(p => ({ ...p, [key]: v }))
                                )}
                              </div>
                            ))}
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateAsset.mutate({ id: asset.id, data: editForm as Record<string, string> })}
                              disabled={!(editForm as Record<string, string>).name || updateAsset.isPending}
                              className="rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                            >
                              {updateAsset.isPending ? 'Salvataggio...' : 'Salva'}
                            </button>
                            <button
                              onClick={() => { setEditingId(null); setEditForm({}) }}
                              className="rounded-lg border border-[var(--fc-border)] px-3 py-1.5 text-xs"
                            >
                              Annulla
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Header prodotto — view mode */
                        <div className="flex items-center gap-3">
                          <button onClick={() => toggleExpand(asset.id)} className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-foreground)]">
                            {expanded[asset.id]
                              ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-[var(--fc-foreground)]">{asset.name}</p>
                              {asset.firmwareFileName && (
                                <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  FW: {asset.firmwareFileName}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-[var(--fc-foreground-muted)] truncate">
                              {[asset.assetType, asset.model, asset.manufacturer].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <FileDropZone
                              label="Firmware"
                              currentName={asset.firmwareFileName}
                              uploading={uploading[asset.id]}
                              onFile={file => handleFileUpload(asset.id, file, 'firmware')}
                            />
                            <button
                              onClick={() => startEditAsset(asset)}
                              className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)]"
                              title="Modifica prodotto"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => { if (confirm('Eliminare prodotto e tutte le sue unità?')) deleteAsset.mutate(asset.id) }}
                              className="text-[var(--fc-foreground-muted)] hover:text-red-500"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Istanze espanse */}
                      {expanded[asset.id] && editingId !== asset.id && (
                        <div className="mt-4 ml-7 space-y-3">
                          {asset.instances.length > 0 && (
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-[var(--fc-border)] text-xs font-medium text-[var(--fc-foreground-muted)] uppercase text-left">
                                    <th className="pb-2 pr-4">S/N</th>
                                    <th className="pb-2 pr-4">P/N</th>
                                    <th className="pb-2 pr-4">Ubicazione</th>
                                    <th className="pb-2 pr-4">Note</th>
                                    <th className="pb-2" />
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-[var(--fc-border)]">
                                  {asset.instances.map(inst => (
                                    editingInstId === inst.id ? (
                                      /* Instance edit row */
                                      <tr key={inst.id}>
                                        <td className="py-2 pr-2">
                                          {inlineInput(editInstForm.serialNumber, v => setEditInstForm(p => ({ ...p, serialNumber: v })), 'S/N', 'font-mono text-xs')}
                                        </td>
                                        <td className="py-2 pr-2">
                                          {inlineInput(editInstForm.partNumber, v => setEditInstForm(p => ({ ...p, partNumber: v })), 'P/N', 'font-mono text-xs')}
                                        </td>
                                        <td className="py-2 pr-2">
                                          {inlineInput(editInstForm.location, v => setEditInstForm(p => ({ ...p, location: v })), 'Ubicazione')}
                                        </td>
                                        <td className="py-2 pr-2">
                                          {inlineInput(editInstForm.notes, v => setEditInstForm(p => ({ ...p, notes: v })), 'Note', 'text-xs')}
                                        </td>
                                        <td className="py-2 whitespace-nowrap">
                                          <div className="flex items-center gap-1">
                                            <button
                                              onClick={() => updateInst.mutate({ assetId: asset.id, instanceId: inst.id, data: editInstForm })}
                                              disabled={updateInst.isPending}
                                              className="rounded bg-[var(--fc-primary)] px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                                            >
                                              {updateInst.isPending ? '...' : 'Salva'}
                                            </button>
                                            <button
                                              onClick={() => { setEditingInstId(null); setEditingInstAssetId(null); setEditInstForm(emptyInst) }}
                                              className="rounded border border-[var(--fc-border)] px-2 py-1 text-xs"
                                            >
                                              Annulla
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ) : (
                                      /* Instance view row */
                                      <tr key={inst.id}>
                                        <td className="py-2 pr-4 font-mono text-xs">{inst.serialNumber ?? '—'}</td>
                                        <td className="py-2 pr-4 font-mono text-xs">{inst.partNumber ?? '—'}</td>
                                        <td className="py-2 pr-4">{inst.location ?? '—'}</td>
                                        <td className="py-2 pr-4 text-[var(--fc-foreground-muted)] text-xs">{inst.notes ?? ''}</td>
                                        <td className="py-2">
                                          <div className="flex items-center gap-2">
                                            <button
                                              onClick={() => startEditInst(inst, asset.id)}
                                              className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)]"
                                              title="Modifica unità"
                                            >
                                              <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                              onClick={() => deleteInst.mutate({ assetId: asset.id, instanceId: inst.id })}
                                              className="text-[var(--fc-foreground-muted)] hover:text-red-500"
                                            >
                                              <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    )
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}

                          {showInstForm[asset.id] ? (
                            <div className="rounded-lg border border-[var(--fc-border)] p-3 space-y-2">
                              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                                {([
                                  { key: 'serialNumber' as const, label: 'S/N' },
                                  { key: 'partNumber' as const, label: 'P/N' },
                                  { key: 'location' as const, label: 'Ubicazione' },
                                  { key: 'notes' as const, label: 'Note' },
                                ]).map(({ key, label }) => (
                                  <div key={key}>
                                    <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">{label}</label>
                                    <input
                                      value={instForm[asset.id]?.[key] ?? ''}
                                      onChange={e => setInstForm(p => ({
                                        ...p,
                                        [asset.id]: { ...emptyInst, ...p[asset.id], [key]: e.target.value },
                                      }))}
                                      className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-2 py-1.5 text-sm font-mono"
                                    />
                                  </div>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => createInst.mutate({ assetId: asset.id, data: instForm[asset.id] ?? emptyInst })}
                                  disabled={createInst.isPending}
                                  className="rounded-lg bg-[var(--fc-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                                >
                                  Aggiungi unità
                                </button>
                                <button
                                  onClick={() => setShowInstForm(p => ({ ...p, [asset.id]: false }))}
                                  className="rounded-lg border border-[var(--fc-border)] px-3 py-1.5 text-xs"
                                >
                                  Annulla
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowInstForm(p => ({ ...p, [asset.id]: true }))}
                              className="flex items-center gap-1 text-xs text-[var(--fc-primary)] hover:underline"
                            >
                              <Plus className="h-3 w-3" /> Aggiungi unità (S/N / P/N)
                            </button>
                          )}
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ---- SOFTWARE TAB ---- */}
          {activeTab === 'software' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button
                  onClick={() => { setShowSWForm(true); setSWFormCompany(selectedCompany); setSWFormSiteId(selectedSite) }}
                  className="flex items-center gap-2 rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white"
                >
                  <Plus className="h-4 w-4" /> Nuovo software
                </button>
              </div>

              {showSWForm && (
                <Card>
                  <h3 className="text-base font-semibold mb-4">Nuovo software</h3>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Cliente</label>
                      <select
                        value={swFormCompany}
                        onChange={e => { setSWFormCompany(e.target.value); setSWFormSiteId('') }}
                        className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                      >
                        <option value="">— seleziona —</option>
                        {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">Sito</label>
                      <select
                        value={swFormSiteId}
                        onChange={e => setSWFormSiteId(e.target.value)}
                        disabled={!swFormCompany}
                        className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm disabled:opacity-50"
                      >
                        <option value="">— seleziona —</option>
                        {formSitesSW.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    {([
                      { key: 'name' as const, label: 'Nome software *' },
                      { key: 'softwareRelease' as const, label: 'Release / versione' },
                      { key: 'hostMachine' as const, label: 'Macchina / Database' },
                    ]).map(({ key, label }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-[var(--fc-foreground-muted)] mb-1">{label}</label>
                        <input
                          value={swForm[key]}
                          onChange={e => setSWForm(p => ({ ...p, [key]: e.target.value }))}
                          className="w-full rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] px-3 py-2 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => createSW.mutate()}
                      disabled={!swForm.name || !swFormCompany || !swFormSiteId || createSW.isPending}
                      className="rounded-lg bg-[var(--fc-primary)] px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {createSW.isPending ? 'Salvataggio...' : 'Salva software'}
                    </button>
                    <button onClick={() => setShowSWForm(false)} className="rounded-lg border border-[var(--fc-border)] px-4 py-2 text-sm">
                      Annulla
                    </button>
                  </div>
                </Card>
              )}

              {isLoading ? (
                <p className="text-sm text-[var(--fc-foreground-muted)]">Caricamento...</p>
              ) : assets.length === 0 ? (
                <p className="text-sm text-[var(--fc-foreground-muted)]">Nessun software. Clicca "Nuovo software" per iniziare.</p>
              ) : (
                <Card>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--fc-border)] text-left text-xs font-medium text-[var(--fc-foreground-muted)] uppercase">
                        <th className="pb-3 pr-4">Nome</th>
                        <th className="pb-3 pr-4">Release</th>
                        <th className="pb-3 pr-4">Macchina / DB</th>
                        <th className="pb-3 pr-4">Backup DB</th>
                        <th className="pb-3" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--fc-border)]">
                      {assets.map(sw => (
                        editingId === sw.id ? (
                          /* Software edit row */
                          <tr key={sw.id}>
                            <td className="py-2 pr-2">
                              {inlineInput(
                                (editForm as Record<string, string>).name ?? '',
                                v => setEditForm(p => ({ ...p, name: v })),
                                'Nome *'
                              )}
                            </td>
                            <td className="py-2 pr-2">
                              {inlineInput(
                                (editForm as Record<string, string>).softwareRelease ?? '',
                                v => setEditForm(p => ({ ...p, softwareRelease: v })),
                                'Release'
                              )}
                            </td>
                            <td className="py-2 pr-2">
                              {inlineInput(
                                (editForm as Record<string, string>).hostMachine ?? '',
                                v => setEditForm(p => ({ ...p, hostMachine: v })),
                                'Macchina / DB'
                              )}
                            </td>
                            <td className="py-2 pr-4">
                              <FileDropZone
                                label="Carica backup"
                                currentName={sw.backupFileName}
                                uploading={uploading[sw.id]}
                                onFile={file => handleFileUpload(sw.id, file, 'backup')}
                              />
                            </td>
                            <td className="py-2 whitespace-nowrap">
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => updateAsset.mutate({ id: sw.id, data: editForm as Record<string, string> })}
                                  disabled={!(editForm as Record<string, string>).name || updateAsset.isPending}
                                  className="rounded bg-[var(--fc-primary)] px-2 py-1 text-xs font-medium text-white disabled:opacity-50"
                                >
                                  {updateAsset.isPending ? '...' : 'Salva'}
                                </button>
                                <button
                                  onClick={() => { setEditingId(null); setEditForm({}) }}
                                  className="rounded border border-[var(--fc-border)] px-2 py-1 text-xs"
                                >
                                  Annulla
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          /* Software view row */
                          <tr key={sw.id}>
                            <td className="py-3 pr-4 font-medium text-[var(--fc-foreground)]">{sw.name}</td>
                            <td className="py-3 pr-4 text-[var(--fc-foreground-muted)]">{sw.softwareRelease ?? '—'}</td>
                            <td className="py-3 pr-4 text-[var(--fc-foreground-muted)]">{sw.hostMachine ?? '—'}</td>
                            <td className="py-3 pr-4">
                              <FileDropZone
                                label="Carica backup"
                                currentName={sw.backupFileName}
                                uploading={uploading[sw.id]}
                                onFile={file => handleFileUpload(sw.id, file, 'backup')}
                              />
                            </td>
                            <td className="py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => startEditAsset(sw)}
                                  className="text-[var(--fc-foreground-muted)] hover:text-[var(--fc-primary)]"
                                  title="Modifica software"
                                >
                                  <Pencil className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => { if (confirm('Eliminare software?')) deleteAsset.mutate(sw.id) }}
                                  className="text-[var(--fc-foreground-muted)] hover:text-red-500"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>
                </Card>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
